/**
 * Attach per-user identity to every first-party API request, by patching
 * `globalThis.fetch` once at startup. The client-runtime builds its HTTP clients
 * through several layers, so the single `fetch` chokepoint is the reliable place;
 * the raw `/api/morrow/*` calls (connect/usage/memory) go through here too.
 *
 * Two transports:
 *  - **Hosted web** (app.morrowos.com → api.morrowos.com, different origins, no
 *    shared cookie): attach the Clerk JWT (`x-clerk-token`) and the owner id
 *    (`x-morrow-owner-id`). The backend verifies them (auth/EnvironmentAuth.ts).
 *  - **Desktop** (renderer on a custom scheme → local loopback backend): attach
 *    the owner id + the desktop bootstrap bearer so the local backend scopes
 *    per-user data (thread ownership, Composio, memory) to the signed-in Clerk
 *    user instead of `null`. Also forward the Clerk token in case the local
 *    backend can verify it.
 *
 * Headers are only added for those two origins — never to Clerk, Composio, etc.
 */
import { getClerkToken, getOwnerId } from "./clerkTokenBridge";
import { getPersistedGuestId } from "./composioConnect";
import { desktopApiOrigin } from "./apiBase";
import { readDesktopPrimaryBearerToken } from "./environments/primary/desktopAuth";

const apiOrigin = (() => {
  const raw = import.meta.env.VITE_HTTP_URL?.trim();
  if (!raw) return null;
  try {
    return new URL(raw).origin;
  } catch {
    return null;
  }
})();

const isDesktop =
  typeof window !== "undefined" &&
  (window as { desktopBridge?: unknown }).desktopBridge !== undefined;

let installed = false;

function applyOwnerHeaders(headers: Headers, ownerId: string | null): void {
  if (ownerId) headers.set("x-morrow-owner-id", ownerId);
  const guestId = getPersistedGuestId();
  if (guestId && ownerId && guestId !== ownerId) {
    headers.set("x-morrow-claim-guest-id", guestId);
  }
}

function requestOrigin(input: RequestInfo | URL): string | null {
  try {
    const rawUrl =
      typeof input === "string"
        ? input
        : input instanceof URL
          ? input.href
          : (input as Request).url;
    return new URL(rawUrl, window.location.href).origin;
  } catch {
    return null;
  }
}

function isMorrowApiRequest(input: RequestInfo | URL): boolean {
  try {
    const rawUrl =
      typeof input === "string"
        ? input
        : input instanceof URL
          ? input.href
          : (input as Request).url;
    return new URL(rawUrl, window.location.href).pathname.startsWith("/api/morrow/");
  } catch {
    return false;
  }
}

// A cold/booting backend (container waking up, Worker rolling out) surfaces as a
// short-lived 502/503/504 or a network error. Retry those transparently so the
// user sees a brief pause instead of a hard error. Only first-party API traffic
// reaches this wrapper (the pass-through branches below call `originalFetch`
// directly), and the auth headers are already attached before we get here.
//
// The set of transient conditions mirrors the source of truth in
// `environments/primary/auth.ts` (`TRANSIENT_BOOTSTRAP_STATUS_CODES` /
// `isTransientBootstrapError`). That predicate is coupled to
// `PrimaryEnvironmentRequestError`, so we keep an identical local copy that
// inspects raw `Response` status codes and thrown fetch errors instead.
const TRANSIENT_STATUS_CODES = new Set([502, 503, 504]);
const RETRY_MAX_ATTEMPTS = 5; // ~4 retries → waits 300+600+1200+2400ms (~4.5s)
const RETRY_BASE_DELAY_MS = 300;
const RETRY_MAX_DELAY_MS = 3_000;

function backoffDelayMs(attempt: number): number {
  return Math.min(RETRY_BASE_DELAY_MS * 2 ** (attempt - 1), RETRY_MAX_DELAY_MS);
}

// Network-level failures fetch surfaces as `TypeError`, plus the `AbortError`
// DOMException. Mirrors `isTransientBootstrapError` in auth.ts.
function isTransientFetchError(error: unknown): boolean {
  if (error instanceof TypeError) return true;
  return error instanceof DOMException && error.name === "AbortError";
}

// Requests we must never buffer/replay: SSE streams, protocol upgrades
// (websockets), a caller-supplied streaming body (can't be re-sent), or a
// request whose signal is already aborted.
function isRetryableRequest(request: Request, bodyIsStream: boolean): boolean {
  if (bodyIsStream) return false;
  if (request.signal?.aborted) return false;
  const accept = request.headers.get("accept");
  if (accept && accept.includes("text/event-stream")) return false;
  if (request.headers.has("upgrade")) return false;
  return true;
}

function waitForRetryBackoff(delayMs: number, signal: AbortSignal | null): Promise<void> {
  return new Promise((resolve) => {
    if (signal?.aborted) {
      resolve();
      return;
    }
    const timer = setTimeout(finish, delayMs);
    const onAbort = () => finish();
    function finish() {
      clearTimeout(timer);
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

/**
 * Perform an API request with retry-and-backoff for transient boot failures.
 *
 * Idempotency safety:
 *  - GET/HEAD retry on both transient status codes AND network errors.
 *  - Non-idempotent methods (POST/PUT/PATCH/DELETE) retry ONLY on a transient
 *    502/503/504 *response* — the server answered with a status, so it did not
 *    process the mutation and replay is safe. A network error is ambiguous (the
 *    request may have been received), so those are never retried to avoid
 *    double-submits.
 *  - A caller-aborted signal stops retries immediately (and short-circuits any
 *    in-flight backoff wait), surfacing the last result to the existing error
 *    path.
 *
 * Exported for unit testing; `request` is cloned per attempt so a buffered body
 * can be re-sent.
 */
export async function fetchWithApiRetry(
  originalFetch: typeof fetch,
  request: Request,
  bodyIsStream = false,
): Promise<Response> {
  const retryable = isRetryableRequest(request, bodyIsStream);
  const method = request.method.toUpperCase();
  const isIdempotent = method === "GET" || method === "HEAD";
  const signal = request.signal ?? null;
  const perform = () => originalFetch(retryable ? request.clone() : request);

  if (!retryable) return perform();

  for (let attempt = 1; ; attempt++) {
    const isLastAttempt = attempt >= RETRY_MAX_ATTEMPTS;
    let response: Response;
    try {
      response = await perform();
    } catch (error) {
      const canRetry = isIdempotent && isTransientFetchError(error);
      if (!canRetry || isLastAttempt || signal?.aborted) throw error;
      await waitForRetryBackoff(backoffDelayMs(attempt), signal);
      if (signal?.aborted) throw error;
      continue;
    }

    if (isLastAttempt || !TRANSIENT_STATUS_CODES.has(response.status) || signal?.aborted) {
      return response;
    }
    await waitForRetryBackoff(backoffDelayMs(attempt), signal);
    if (signal?.aborted) return response;
  }
}

export function installApiFetchAuth(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;

  const originalFetch = globalThis.fetch.bind(globalThis);

  const withHeaders = (
    input: RequestInfo | URL,
    init: RequestInit | undefined,
    apply: (headers: Headers) => void,
  ): Promise<Response> => {
    const headers = new Headers(
      init?.headers ?? (input instanceof Request ? input.headers : undefined),
    );
    apply(headers);
    // Normalize to a single Request so the retry wrapper can clone it per
    // attempt. A caller-supplied streaming body can't be replayed, so flag it.
    if (input instanceof Request) {
      return fetchWithApiRetry(originalFetch, new Request(input, { headers }));
    }
    const bodyIsStream = init?.body instanceof ReadableStream;
    return fetchWithApiRetry(originalFetch, new Request(input, { ...init, headers }), bodyIsStream);
  };

  const withClerkAuth = (
    input: RequestInfo | URL,
    init: RequestInit | undefined,
  ): Promise<Response> => {
    return (async () => {
      const token = await getClerkToken();
      const ownerId = getOwnerId();
      if (!token && !ownerId) return originalFetch(input, init);
      return withHeaders(input, init, (headers) => {
        if (token) headers.set("x-clerk-token", token);
        applyOwnerHeaders(headers, ownerId);
      });
    })();
  };

  globalThis.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const origin = requestOrigin(input);
    const sameOrigin = origin === window.location.origin;

    // Hosted web: cross-origin API needs the Clerk token + owner id on every call.
    if (apiOrigin !== null && origin === apiOrigin) {
      return withClerkAuth(input, init);
    }

    // Desktop: the local loopback backend scopes per-user data off the owner id;
    // the bootstrap bearer authenticates the desktop session.
    if (isDesktop && origin !== null && origin === desktopApiOrigin()) {
      const ownerId = getOwnerId();
      const bearer = await readDesktopPrimaryBearerToken().catch(() => null);
      const token = await getClerkToken().catch(() => null);
      if (!ownerId && !bearer && !token) return originalFetch(input, init);
      return withHeaders(input, init, (headers) => {
        applyOwnerHeaders(headers, ownerId);
        if (token) headers.set("x-clerk-token", token);
        if (bearer && !headers.has("authorization")) {
          headers.set("authorization", `Bearer ${bearer}`);
        }
      });
    }

    // Single-origin dev (Vite proxy): morrow routes still need Clerk/owner headers.
    if (sameOrigin && isMorrowApiRequest(input)) {
      return withClerkAuth(input, init);
    }

    return originalFetch(input, init);
  };
}
